import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, DollarSign, Users, Ban, Copyright, Scale } from 'lucide-react';
import { SEO } from '../components/SEO';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="terms-page">
      <SEO title="Terms of Service" description="Read the terms and conditions for using Supremics." />
      
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-[var(--surface)] to-[var(--background)] border-b border-[var(--border)]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-[var(--secondary)]/5 rounded-full blur-3xl" />
        </div>
        <div className="relative page-padding py-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="text-[var(--primary)]" size={40} />
          </div>
          <h1 className="text-4xl md:text-5xl font-teko font-semibold mb-4">Terms of Service</h1>
          <p className="text-[var(--text-secondary)] max-w-xl mx-auto">
            Last updated: February 2, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="page-padding py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-8 space-y-8">
            
            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Agreement to Terms
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                By accessing or using Supremics, you agree to be bound by these Terms of Service. 
                If you disagree with any part of these terms, you may not access our service. 
                These terms apply to all visitors, users, and others who access or use the service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                <Users className="text-[var(--primary)]" size={24} />
                User Accounts
              </h2>
              <ul className="space-y-3 text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  You must be at least 13 years old to use this service
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  You are responsible for maintaining the security of your account
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  You must provide accurate and complete information
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  One person may not have more than one free account
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full mt-2" />
                  You may not use your account for any illegal purpose
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                <Copyright className="text-[var(--primary)]" size={24} />
                Content Ownership
              </h2>
              <div className="space-y-4 text-[var(--text-secondary)]">
                <p>
                  <strong className="text-[var(--foreground)]">Your Content:</strong> You retain all rights to the comics, 
                  artwork, and other content you upload to Supremics. By uploading content, you grant us a 
                  non-exclusive license to display, distribute, and promote your work on our platform.
                </p>
                <p>
                  <strong className="text-[var(--foreground)]">Our Content:</strong> The Supremics service, including its 
                  original content, features, and functionality, is owned by Supremics and protected by 
                  international copyright, trademark, and other intellectual property laws.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                <Ban className="text-red-500" size={24} />
                Prohibited Content
              </h2>
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-[var(--text-secondary)] mb-3">The following content is strictly prohibited:</p>
                <ul className="space-y-2 text-[var(--text-secondary)]">
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-1" />
                    Content that exploits or endangers minors
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-1" />
                    Hate speech, harassment, or discrimination
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-1" />
                    Copyright infringing material
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-1" />
                    Spam, malware, or deceptive content
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-1" />
                    Content promoting violence or illegal activities
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                <DollarSign className="text-green-500" size={24} />
                Payments & Subscriptions
              </h2>
              <ul className="space-y-3 text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" />
                  Subscriptions automatically renew unless cancelled before the renewal date
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" />
                  Refunds are available within 7 days of purchase for unused subscriptions
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" />
                  Creator earnings are paid out monthly via Stripe Connect
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" />
                  We reserve the right to change pricing with 30 days notice
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2" />
                  Tips and donations are non-refundable once processed
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                <Scale className="text-[var(--primary)]" size={24} />
                Limitation of Liability
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                Supremics shall not be liable for any indirect, incidental, special, consequential, or 
                punitive damages resulting from your use of the service. Our total liability shall not 
                exceed the amount you have paid us in the past twelve months. Some jurisdictions do not 
                allow limitations on implied warranties, so these limitations may not apply to you.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Account Termination
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                We may terminate or suspend your account immediately, without prior notice, for:
              </p>
              <ul className="space-y-2 text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mt-2" />
                  Violation of these Terms of Service
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mt-2" />
                  Fraudulent or illegal activity
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mt-2" />
                  Repeated copyright violations
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 bg-[var(--secondary)] rounded-full mt-2" />
                  At our sole discretion for any reason
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Changes to Terms
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                We reserve the right to modify these terms at any time. We will notify users of 
                material changes via email or through the service. Your continued use of Supremics 
                after such modifications constitutes your acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-teko flex items-center gap-2 mb-4">
                <span className="w-1 h-6 bg-[var(--primary)] rounded-full" />
                Contact
              </h2>
              <div className="p-4 bg-[var(--background)] rounded-xl border border-[var(--border)]">
                <p className="text-[var(--foreground)]">For questions about these Terms:</p>
                <p className="text-[var(--text-secondary)]">Email: legal@supremics.com</p>
              </div>
            </section>
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-[var(--primary)] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
